/**
 * Server-Sent Events (SSE) client and debounce logic for real-time notifications.
 *
 * This implementation is designed to work with the backend NotificationApiController,
 * which authenticates users via cookies and streams events from `/api/notification/sse`.
 *
 * **Design Notes:**
 * - Event names are intentionally NOT used for dispatching. Instead, all events are handled
 *   via the global event handler (`_all_`). This is because event names proved unreliable
 *   in practice (see backend and SSE protocol limitations).
 * - The server always sends a consistent object structure in the event packet, and the client
 *   expects to parse and handle this object regardless of the event name.
 * - This approach ensures robust delivery and handling of all notification types, even if
 *   the event name is missing, malformed, or not supported by the browser/EventSource polyfill.
 *
 * **Backend Reference:**
 * - See NotificationApiController.cs for the server-side implementation and rationale.
 * - The backend endpoint `/api/notification/sse` maintains the connection and streams events
 *   as authenticated by the user's session cookie.
 */
import { activeUserProfile } from '../../active-userprofile';
import { globalState } from '../../global-state';
import { ApiFactory } from '../../api/agwasuite-api-factory';
import { NotificationApi } from '../../api/notification/notification-api';
import type { NotificationTopicsBatchResult } from '../../api/agwa-api-model';
// (debounce import removed; reconnect now uses explicit scheduling)
import { readTabSessionId } from '../session-storage-keys';
import { ensureTabSessionId } from '../../services/tab-session';
import { replaceTerms } from '../../language/lang';
import { REFUSED_TOPIC_SENTINEL } from './refused-topic-sentinel';
import {
  CancellationToken,
  defaultDebounceDuration,
  defaultTestInterval,
  getElapsedMilliseconds,
} from './debounce-event';

/**
 * Handler function for SSE events. Receives the parsed event data object and the event name.
 *
 * Note: The event name is not used for dispatching; all events are handled globally.
 */
export type EventSourceHandler = (data: unknown, eventName: string) => void;
/**
 * Options for configuring debounced SSE event execution.
 *
 * - `event`: The main function to execute after the debounce threshold.
 * - `threshold`: Time in ms to wait before executing the event.
 * - `title`: Optional label for logging/debugging.
 * - `eventTriggered`: Called when the event is first triggered (with data).
 * - `eventFinally`: Called after the event completes or is cancelled.
 * - `eventActioned`: Called each time the event is retriggered during the debounce window.
 * - `cancelToken`: Optional cancellation token.
 * - `testInterval`: How often to check for threshold completion (ms).
 *
 * This pattern is used to coalesce rapid-fire events into a single action, reducing
 * redundant processing and improving UX for real-time notifications.
 */
export interface SSEDebounceExecutionOptions {
  event: () => void | Promise<void>;
  threshold?: number;
  title?: string;
  eventTriggered?: EventSourceHandler;
  eventFinally?: () => void;
  eventActioned?: EventSourceHandler;
  cancelToken?: CancellationToken;
  testInterval?: number;
}

//staged execution should only be used in cases where recieving a message is
//enough info and the data is not important
/**
 * Creates a debounced handler for SSE events.
 *
 * This function is used to debounce rapid event triggers from the SSE stream.
 * It is designed for use cases where receiving a message is sufficient and the
 * specific data is not important for each trigger.
 *
 * **Design:**
 * - All events are handled via the global handler, not by event name.
 * - The server sends a consistent object structure in each event packet.
 * - The handler coalesces multiple triggers into a single execution after the threshold.
 *
 * @param options Debounce configuration options
 * @returns An event handler function for use with SSEClient
 */
export function createSseDebounceExecution(
  options: SSEDebounceExecutionOptions
): EventSourceHandler {
  //create a closure that will execute on a timed interval
  const intervalms = options.testInterval ?? defaultTestInterval;
  let timer: ReturnType<typeof setTimeout> | undefined = undefined;
  let triggered: Date | undefined = undefined;
  let actioned: Date | undefined = undefined;
  let actionCount = 0;
  let eventActionQueue: { data: unknown; eventName: string }[] = [];
  let executing = false;
  let restart = false;

  const reset = () => {
    clearInterval(timer);
    timer = undefined;
    triggered = undefined;
    actionCount = 0;
    actioned = undefined;
  };
  const intervalEvent = async () => {
    if (options.cancelToken?.cancelled) {
      reset();
      options.cancelToken?.reset();
      options.eventFinally?.();
      return;
    }
    const ms = getElapsedMilliseconds(triggered!);
    if (ms >= (options.threshold ?? defaultDebounceDuration)) {
      try {
        executing = true;
        await options.event();
        options.eventFinally?.();
      } finally {
        executing = false;
        reset();
        if (restart) {
          try {
            options.eventTriggered?.(
              eventActionQueue[0].data,
              eventActionQueue[0].eventName
            );
            eventActionQueue
              .filter((_x, index) => index > 0)
              .forEach((x) => {
                options.eventActioned?.(x.data, x.eventName);
              });
          } finally {
            eventActionQueue = [];
          }

          startProcess();
        }
      }
      return;
    }
    timer = setTimeout(intervalEvent, intervalms);
  };
  return (data: unknown, eventName: string) => {
    console.log(`staged ${eventName} with ${data}`);
    if (!triggered) {
      options.eventTriggered?.(data, eventName);
      options.eventActioned?.(data, eventName);
      startProcess();
    } else {
      if (executing) restart = true;
      actionCount++;
      actioned = new Date();
      if (executing) eventActionQueue.push({ data, eventName });
      else options.eventActioned?.(data, eventName);
      console.log(
        `Triggered Event '${options.title ?? 'no name'}' actioned ${actionCount} since ${triggered.toLocaleTimeString()}, at ${actioned.toLocaleTimeString()}`
      );
    }
  };

  function startProcess() {
    executing = false;
    restart = false;
    options.cancelToken?.reset();
    triggered = new Date();
    actionCount++;
    actioned = triggered;

    timer = setTimeout(intervalEvent, intervalms);
    console.log(
      `Triggered Event '${options.title ?? 'no name'}' started ${triggered.toLocaleTimeString()}`
    );
  }
}
/**
 * What happened to the live-update connection: it opened, it failed (a reconnect is scheduled), or
 * it was closed on purpose (nothing will retry).
 */
export type SseConnectionEvent = 'open' | 'error' | 'closed';

export interface SSEClientInitializer {
  /**
   * callback to indicate online offline status
   * @param value state of the instance
   * @returns
   */
  onlineCallback: (value: boolean) => void;
  afterOnlineEvent: (() => Promise<void>) | (() => void);
  url: string;
}

(function () {
  globalState().sseClients = globalState().sseClients ?? {};
})();

const _all_ = '__all__';
/**
 * SSEClient manages the connection to the backend notification SSE endpoint.
 *
 * - Connects to `/api/notification/sse` using cookie-based authentication.
 * - Handles reconnection with exponential backoff.
 * - All events are dispatched to global handlers (not by event name).
 * - Designed to be robust against event name issues and browser quirks.
 *
 * See NotificationApiController.cs for backend details.
 */
export class SSEClient {
  /**
   * Registers an event handler for all incoming SSE events.
   *
   * Note: Event names are not used for dispatching. All events are handled globally.
   *
   * @param eventNames Ignored; use '' or any string, all events are handled the same.
   * @param event Handler function to call with event data.
   */
  /**
   * Removes an event handler from the global event handler list.
   *
   * Note: Event names are not used for dispatching. All events are handled globally.
   *
   * @param eventNames Ignored; use '' or any string, all events are handled the same.
   * @param event Handler function to remove.
   */
  private _initializer: SSEClientInitializer;
  private static _instances(): { [key: string]: SSEClient } {
    return globalState().sseClients as {
      [key: string]: SSEClient;
    };
  }
  userId: string = '00000000-0000-0000-0000-000000000000';
  public static getInstance(
    instanceName?: string,
    factoryOptions?: SSEClientInitializer
  ): SSEClient {
    const key = instanceName ?? 'default';
    let existing = SSEClient._instances()[key];
    if (!existing) {
      if (!factoryOptions) {
        // Create a placeholder instance so early imports (e.g., sse-hub) do not crash the app.
        // This will be upgraded when real factoryOptions are later provided.
        const placeholder: SSEClientInitializer = {
          url: window.location.origin,
          onlineCallback: () => {},
          afterOnlineEvent: () => {},
        };
        const inst = new SSEClient(placeholder) as SSEClient & {
          __placeholder?: boolean;
        };
        inst.__placeholder = true;
        SSEClient._instances()[key] = inst;
        existing = inst;
      } else {
        SSEClient._instances()[key] = new SSEClient(factoryOptions);
        existing = SSEClient._instances()[key];
      }
    } else if (factoryOptions) {
      // If a late real initializer arrives and current is placeholder, promote it.
      const maybePlaceholder = existing as SSEClient & {
        __placeholder?: boolean;
      };
      if (maybePlaceholder.__placeholder) {
        (
          maybePlaceholder as unknown as { _initializer: SSEClientInitializer }
        )._initializer = factoryOptions;
        delete maybePlaceholder.__placeholder;
      }
    }
    return existing;
  }
  private _source?: EventSource;
  private _mode: 'anonymous' | 'user' = 'anonymous';
  private _events: { [key: string]: EventSourceHandler[] } = {};
  /**
   * This tab's session, or empty until the server has issued one.
   *
   * READ, NEVER MINTED. This class used to generate a GUID here and that was the defect: a session
   * the caller chooses cannot decide who holds a lock. The stream is now one CONSUMER of a
   * server-issued value - it puts it in the connect URL only because EventSource cannot set
   * headers - and `services/tab-session.ts` owns obtaining it (#366, D-114).
   */
  private _sessionId: string;
  // Tracks the user session ID that was active when the SSE connection was established.
  // Used to detect user context changes and regenerate SSE session ID accordingly.
  private _connectedUserSession: string | null = null;
  // Local topic state (server loses topic registrations on reconnect because a new SSE connection instance is created).
  // We MUST re-send topic registrations after each successful onopen.
  // Reference counts for requested topics (allows multiple parts of the app to subscribe
  // to the same topic independently). A topic is actually registered server-side only
  // when its ref count transitions from 0 -> 1, and removed when it transitions 1 -> 0.
  private _topicRefCounts: Map<string, number> = new Map();
  private _inflightTopicOps: Map<string, 'add' | 'remove'> = new Map();
  // Tracks topics we believe are currently registered on the server for this live connection.
  // Cleared on reconnect (since server-side session state for topics is tied to the SSE connection lifecycle).
  private _serverTopics: Set<string> = new Set();
  // Batch + retry state
  private _pendingBatch: Map<string, 'add' | 'remove'> = new Map();
  private _batchTimer: ReturnType<typeof setTimeout> | undefined;
  private _batchDelayMs = 10; // short debounce for grouping rapid operations
  private _batchRetryAttempt = 0;
  private _isFlushingBatch = false;
  /**
   * A failed batch is waiting out its retry backoff (#398 C14 d). While it is, a newly queued topic
   * rides that retry instead of rescheduling the flush at the 10 ms batch delay, which cut the
   * backoff short and asked a server that had just said it could not serve again at once.
   */
  private _retryPending = false;
  /**
   * Topics whose add is sent and not yet answered (#398 C14 d). The client does not yet believe the
   * server holds them, so without this an unsubscribe in that window sent no remove, and the add
   * then landing left the server delivering a topic nobody on the page wanted.
   */
  private _inflightAdds: Set<string> = new Set();
  private _baseRetryDelayMs = 250;
  private _maxBatchRetryDelayMs = 5000;

  /**
   * Who wants to be told that a topic was refused. In practice this is `SseHub`, once.
   *
   * A SET rather than a single slot because nothing about a refusal is the hub's alone, and a
   * second listener must not silently displace the first.
   */
  private _topicRefusedListeners: Set<(topic: string) => void> = new Set();

  /**
   * Be told when the server refuses a topic, so a layer above can drop its own record of it.
   *
   * THE CLIENT PUSHES RATHER THAN THE LAYER ABOVE POLLING, because a refusal is an event with a
   * moment: it arrives on one batch answer and is never repeated, so there is nothing for a
   * poller to find afterwards - by the time anything looked, this class has already swept its
   * three stores and the topic reads exactly like one that was never asked for.
   *
   * Returns a disposer, the same shape `SseHub.subscribeTopic` hands its callers.
   *
   * @param listener called with the topic exactly as the refusal named it, after this class has
   * finished its own sweep, so a listener always observes a client that already holds nothing.
   */
  public onTopicRefused(listener: (topic: string) => void): () => void {
    this._topicRefusedListeners.add(listener);
    return () => {
      this._topicRefusedListeners.delete(listener);
    };
  }

  /**
   * Tell every listener, and never let one of them take down the sweep or the marker.
   *
   * The same guarding as `SseHub.routeRaw` uses around its own handlers: a listener is somebody
   * else's code, and a refusal is already a defect being reported - losing the marker because the
   * reporting path threw would remove the one signal that says so.
   */
  private _notifyTopicRefused(topic: string) {
    this._topicRefusedListeners.forEach((listener) => {
      try {
        listener(topic);
      } catch (err) {
        console.warn('Topic refusal listener error', err);
      }
    });
  }

  constructor(factoryOptions: SSEClientInitializer) {
    this._initializer = factoryOptions;
    // Whatever this tab has already been issued. A reload keeps it (D-136), but it does NOT carry
    // the tab's locks across: the unloading page released every lock the session held on pagehide
    // (D-116), so a reloaded tab holds nothing until it takes a lock again. Empty when the tab has
    // not been issued one yet, and while a reload is still deciding whether to keep the id it found;
    // connectAppropriate asks for it, because asking is asynchronous and a constructor cannot wait.
    this._sessionId = readTabSessionId() ?? '';
  }

  /**
   * Clear topic state when switching users or logging out.
   * Note: We do NOT regenerate the session ID here - it stays stable to preserve lock ownership.
   * Topic isolation is achieved because:
   * 1. The server creates a new EventSourceService instance per SSE connection with empty topics
   * 2. We clear local tracking and resubscribe for the new context
   */
  private _clearTopicState() {
    // Clear server-side topic tracking (server-side is already cleared when connection closes/reopens)
    this._serverTopics.clear();
    this._inflightTopicOps.clear();
    this._inflightAdds.clear();
    this._pendingBatch.clear();
    this._retryPending = false;
    if (this._batchTimer) {
      clearTimeout(this._batchTimer);
      this._batchTimer = undefined;
    }
    // Local ref counts are KEPT: they are the hub's logical subscriptions, and app-lifetime
    // subscribers (the state-colour overrides loader, any module-level listener) never
    // re-subscribe on a user switch - only page components do, on remount. Clearing them here
    // meant the reconnect's _resubscribeTopics had nothing to re-add, so a session that signed
    // out and back in stopped receiving those topics until a hard reload (2026-09-06 review
    // finding). Server-side tracking above is what the new connection must rebuild; the
    // reconnect re-adds every topic still holding a reference.
  }

  reconnectFrequencySeconds = 1;
  private _baseReconnectSeconds = 1;
  private _maxReconnectSeconds = 64;
  private _jitterRatio = 0.3; // 30% jitter
  private _reconnectTimer?: ReturnType<typeof setTimeout>;
  private _nextReconnectAt?: number; // epoch ms of next attempt
  // Set once by stopForGood, when the new-version gate is shown; no connection opens after it.
  private _stoppedForGood = false;

  /** Exposed for UI countdowns (e.g., offline overlay). */
  public get nextReconnectAt(): number | undefined {
    return this._nextReconnectAt;
  }

  /** Schedule a reconnect attempt using jittered exponential backoff. */
  private _scheduleReconnect() {
    if (this._stoppedForGood) return;
    if (this._reconnectTimer) clearTimeout(this._reconnectTimer);
    const baseMs = this.reconnectFrequencySeconds * 1000;
    const spread = baseMs * this._jitterRatio;
    const jittered = baseMs + (Math.random() * 2 - 1) * spread;
    const delay = Math.max(250, jittered);
    this._nextReconnectAt = Date.now() + delay;
    this._reconnectTimer = setTimeout(() => {
      this._nextReconnectAt = undefined; // now attempting
      // The timer has fired, so nothing is scheduled any more. Without this the connect below
      // would find a stale timer and report the connection as closed on purpose, mid-retry.
      this._reconnectTimer = undefined;
      // Not awaited - a timer callback cannot wait, and a reconnect that has to obtain a session
      // first simply connects a moment later.
      void this.connectAppropriate();
      this.reconnectFrequencySeconds = Math.min(
        this.reconnectFrequencySeconds * 2,
        this._maxReconnectSeconds
      );
    }, delay);
  }

  /** Reset reconnect backoff (e.g., after a clean successful connection) */
  private _resetReconnectBackoff() {
    this.reconnectFrequencySeconds = this._baseReconnectSeconds;
  }

  /**
   * Who wants to know the connection opened, failed or was closed. In practice the SSE adapter
   * behind `LiveUpdateTransport` (sse-live-update-transport.ts), which turns these into the three
   * states the connection-health monitor reads (D-137). Raw events rather than those states, so
   * this class stays ignorant of the monitor and the adapter owns the mapping.
   */
  private _connectionListeners: Set<(event: SseConnectionEvent) => void> =
    new Set();

  /** Be told when the connection opens, fails or is closed. Returns a disposer. */
  public onConnectionEvent(
    listener: (event: SseConnectionEvent) => void
  ): () => void {
    this._connectionListeners.add(listener);
    return () => {
      this._connectionListeners.delete(listener);
    };
  }

  /** Tell every listener; a listener that throws never stops the connection's own handling. */
  private _notifyConnection(event: SseConnectionEvent) {
    this._connectionListeners.forEach((listener) => {
      try {
        listener(event);
      } catch (err) {
        console.warn('SSE connection listener error', err);
      }
    });
  }

  /**
   * Connect now, abandoning the wait of any scheduled reconnect and starting its backoff again.
   * Called when the server has just become reachable again after being offline (D-137), so live
   * updates come back with the server instead of up to a minute later when the backoff next fires.
   */
  public reconnectNow(): void {
    if (this._stoppedForGood) return;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }
    this._nextReconnectAt = undefined;
    this._resetReconnectBackoff();
    void this.connectAppropriate();
  }

  public static pushEventToDefault(eventName: string, data: unknown) {
    SSEClient.getInstance().pushEvent(eventName, data);
  }
  public pushEvent(eventName: string, data: unknown) {
    this._events[eventName]?.forEach((event) => {
      event(data, eventName);
    });
    this._events[_all_]?.forEach((event) => {
      event(data, eventName);
    });
  }
  private _wireSourceHandlers() {
    if (!this._source) return;
    this._source.onopen = () => {
      this._resetReconnectBackoff();
      // Clear any scheduled reconnect (now connected)
      if (this._reconnectTimer) {
        clearTimeout(this._reconnectTimer);
        this._reconnectTimer = undefined;
      }
      this._nextReconnectAt = undefined;
      this._initializer.onlineCallback(true);
      this._notifyConnection('open');
      try {
        void this._initializer.afterOnlineEvent?.();
      } finally {
        if (this._mode === 'user') {
          // Reset server topic knowledge on each (re)open for user mode only.
          this._serverTopics.clear();
          this._resubscribeTopics();
        }
      }
    };
    this._source.onmessage = (event) => {
      // Anonymous mode currently only receives keep-alive comments; defensive parse.
      try {
        const data = JSON.parse(event.data);
        const eventName = data.eventName;
        this._events[eventName]?.forEach((event1) => event1(data, eventName));
        this._events[_all_]?.forEach((event1) => event1(data, eventName));
      } catch {
        /* swallow non-JSON (keep-alive comments) */
      }
    };
    this._source.onerror = (_err) => {
      console.log('[SSE] error', _err, 'mode=', this._mode);
      // Note: We do NOT regenerate session ID on error - that only happens on user context change.
      // This allows the same SSE session to reconnect and resume topic subscriptions.
      this._initializer.onlineCallback(false);
      this._source?.close();
      this._source = undefined;
      this._scheduleReconnect();
      this._notifyConnection('error');
    };
  }

  /** Establish an authenticated (user) connection. */
  private _connectUser(exitIfConnected = false) {
    // Also the landing point of a connectAppropriate that was waiting on its tab session when the
    // stream was stopped for good.
    if (this._stoppedForGood) return;
    const userSession = sessionStorage.getItem('agwa-active-account') ?? '';

    // Check if we're switching to a different user session
    if (
      this._connectedUserSession !== null &&
      this._connectedUserSession !== userSession
    ) {
      // User context changed - clear topic state but keep session ID stable for lock ownership
      this._clearTopicState();
    }

    if (
      exitIfConnected &&
      this._source &&
      this._mode === 'user' &&
      this._connectedUserSession === userSession
    )
      return;
    const user = activeUserProfile();
    if (!user) return; // silently ignore if no user (caller should fall back to anonymous)
    this.disconnect();
    this._mode = 'user';
    this._connectedUserSession = userSession;

    // Include both SSE session ID and user session ID for authentication
    // usersession is the per-tab user session from sessionStorage
    // sessionid is the SSE connection session for topic management
    const sourceUrl = `${this._initializer.url}/api/notification/sse?sessionid=${this._sessionId}&usersession=${encodeURIComponent(userSession)}`;
    this._source = new EventSource(sourceUrl, { withCredentials: true });
    this._wireSourceHandlers();
  }

  /** Establish (or maintain) an anonymous heartbeat connection. */
  private _connectAnonymous(exitIfConnected = false) {
    if (this._stoppedForGood) return;
    if (exitIfConnected && this._source && this._mode === 'anonymous') return;
    // If we already have an anonymous connection, nothing to do.
    if (this._source && this._mode === 'anonymous') return;

    // Transitioning from user to anonymous (logout) - clear topic state but keep session ID stable
    if (this._connectedUserSession !== null) {
      this._clearTopicState();
    }

    this.disconnect();
    this._mode = 'anonymous';
    this._connectedUserSession = null;
    const sourceUrl = `${this._initializer.url}/api/notification/sse?sessionid=${this._sessionId}`;
    this._source = new EventSource(sourceUrl, { withCredentials: true });
    this._wireSourceHandlers();
  }

  /**
   * Public orchestrator: ensure we are connected in the correct mode (anonymous vs user).
   *
   * <p>THIS IS WHERE A TAB THAT NEVER SIGNED IN GETS ITS SESSION, and it is why the method became
   * asynchronous. The auth cookie is browser-wide, so only the first tab ever runs a login; every
   * tab after it is already authenticated and nothing on its path would otherwise hand it a
   * session. Here it is signed in, so it can ask.</p>
   *
   * <p>Only when signed in. An anonymous visitor cannot be issued a session - it is written in the
   * caller's name and there is no caller - and does not need one, because it holds no locks.</p>
   */
  public async connectAppropriate(): Promise<void> {
    if (this._stoppedForGood) return;
    const user = activeUserProfile();
    if (user?.isLoggedIn) {
      // Awaited BEFORE connecting so the connect URL carries the session. Connecting first and
      // adopting the session afterwards would register the stream under an empty session, and the
      // topic calls that follow would not belong to the tab that made them.
      this._sessionId = (await ensureTabSessionId()) ?? '';
      this._connectUser(true);
    } else {
      this._connectAnonymous(true);
    }
  }

  /** Backward compat retained for existing call sites (user connect only). */
  connectUser(exitIfConnected = false) {
    this._connectUser(exitIfConnected);
  }
  /**
   * The session this tab is connected under.
   *
   * <p>KEPT FOR THE STREAM'S OWN USE - the connect URL and the topic-registration calls, which must
   * quote the session the CONNECTION was made under. It is no longer the way the rest of the app
   * asks "which tab am I": that is `ApiFactory.getSessionId()` / `readTabSessionId()`, which read
   * the stored value directly and do not depend on the stream having connected.</p>
   */
  public get sessionId(): string {
    return this._sessionId;
  }

  /**
   * Get a summary of topic subscription state for debugging.
   */
  public getTopicSummary(): {
    localTopics: Array<{ topic: string; refCount: number }>;
    serverTopics: string[];
    pendingBatch: Array<{ topic: string; action: 'add' | 'remove' }>;
    mode: 'user' | 'anonymous';
    isConnected: boolean;
  } {
    const localTopics: Array<{ topic: string; refCount: number }> = [];
    for (const [topic, refCount] of this._topicRefCounts.entries()) {
      localTopics.push({ topic, refCount });
    }
    localTopics.sort((a, b) => a.topic.localeCompare(b.topic));

    const serverTopics = Array.from(this._serverTopics).sort();

    const pendingBatch: Array<{ topic: string; action: 'add' | 'remove' }> = [];
    for (const [topic, action] of this._pendingBatch.entries()) {
      pendingBatch.push({ topic, action });
    }

    return {
      localTopics,
      serverTopics,
      pendingBatch,
      mode: this._mode,
      isConnected: this._isOpen(),
    };
  }

  /**
   * Subscribe the current session to a topic. The subscription is persisted locally and will be
   * re-applied automatically after reconnects. If the client is offline, the topic is queued and
   * sent once a connection opens.
   * @param topic topic name
   */
  public async subscribeTopic(topic: string): Promise<boolean> {
    topic = topic.trim();
    if (!topic) return false;

    const current = this._topicRefCounts.get(topic) ?? 0;
    const next = current + 1;
    this._topicRefCounts.set(topic, next);

    // Only perform network add when transitioning 0 -> 1.
    if (current > 0) return true; // already logically subscribed

    // Anonymous mode: register locally but skip network call; will subscribe on upgrade to user mode
    if (this._mode === 'anonymous') return true;

    // If a remove is in-flight, we let it finish then resubscribe (simpler than cancellation).
    // We could optimistically mark add, but race window is acceptable.
    if (this._isOpen()) this._queueBatch(topic, 'add');
    // Offline: will resubscribe on connect
    return true;
  }

  /**
   * Unsubscribe the current session from a topic. Removes the topic from the local set and attempts
   * to deregister on the server (best effort). If offline, removal is local only.
   * @param topic topic name
   */
  public async unsubscribeTopic(topic: string): Promise<boolean> {
    topic = topic.trim();
    if (!topic) return false;
    const current = this._topicRefCounts.get(topic);
    if (current === undefined) return true; // nothing to do
    const next = current - 1;
    if (next > 0) {
      this._topicRefCounts.set(topic, next);
      return true; // still in use elsewhere
    }
    // Transition to zero: remove tracking + notify server (if connected & previously registered)
    this._topicRefCounts.delete(topic);
    // Anonymous mode: just update local state, skip network call
    if (this._mode === 'anonymous') return true;
    if (this._inflightTopicOps.get(topic) === 'remove') return true; // already removing
    // Held, or about to be: an add still in flight is answered before the queued remove is sent
    // (the flush's follow-up), so the remove takes off what the add put on.
    const serverHas =
      this._serverTopics.has(topic) || this._inflightAdds.has(topic);
    if (!serverHas) return true; // server already not subscribed (or offline scenario)
    if (this._isOpen()) this._queueBatch(topic, 'remove');
    return true; // offline; treat as success locally
  }

  /**
   * Internal helper to POST topic changes to the backend controller.
   * Relies on cookie auth; assumes same-origin (initializer.url) is correct base path.
   */

  /**
   * Re-apply all locally tracked topics after reconnect. Best effort; failures are logged and retried on next reconnect.
   */
  private _resubscribeTopics() {
    if (!this._isOpen()) return;
    this._topicRefCounts.forEach((_count, topic) => {
      // Only re-add if ref count > 0 (it always is for map entries) and not already confirmed.
      if (!this._serverTopics.has(topic)) this._queueBatch(topic, 'add');
    });
    // Force earlier flush on reconnect for snappier delivery
    this._batchRetryAttempt = 0;
    void this._flushBatch();
  }

  private _queueBatch(topic: string, op: 'add' | 'remove') {
    // Last write wins; add followed by remove before flush cancels out and vice versa
    this._pendingBatch.set(topic, op);
    // A retry waiting out its backoff will send this too; rescheduling would cut the backoff short.
    if (this._retryPending) return;
    this._scheduleBatchFlush(this._batchDelayMs);
  }

  private async _flushBatch() {
    // A flush already in flight owns the batch; anything queued meanwhile is sent by the follow-up
    // its finally schedules (U52).
    if (this._isFlushingBatch) return;
    // The backoff, if there was one, has ended: this is its retry, or a reconnect that supersedes it.
    this._retryPending = false;
    if (!this._isOpen()) return;
    if (this._pendingBatch.size === 0) return;
    this._isFlushingBatch = true;
    // Whether this flush ended by scheduling its own retry, whose backoff the follow-up must keep.
    let retryScheduled = false;
    const adds: string[] = [];
    const removes: string[] = [];
    for (const [t, op] of this._pendingBatch.entries()) {
      if (op === 'add') {
        adds.push(t);
      } else {
        removes.push(t);
      }
    }
    this._pendingBatch.clear();
    // Remove cancellations (should be rare due to last-write-wins)
    const cancel = new Set<string>();
    for (const a of adds) if (removes.includes(a)) cancel.add(a);
    const finalAdds = adds.filter((a) => !cancel.has(a));
    const finalRemoves = removes.filter((r) => !cancel.has(r));
    if (finalAdds.length === 0 && finalRemoves.length === 0) {
      this._isFlushingBatch = false;
      return;
    }
    for (const a of finalAdds) this._inflightAdds.add(a);
    try {
      // Use centralized API client for auth headers, correlation id, retries, etc.
      const api = ApiFactory.authenticated(NotificationApi, {
        baseUrl: this._initializer.url,
      });
      const resp = await api.manageTopics({
        sessionId: this._sessionId,
        add: finalAdds.length ? finalAdds : undefined,
        remove: finalRemoves.length ? finalRemoves : undefined,
      });
      if (!resp.success) {
        console.warn(
          `Batch topic update failed status=${resp.status} error=${resp.error}`
        );
        this._requeueFailed(finalAdds, finalRemoves);
        this._scheduleRetry();
        retryScheduled = true;
        return;
      }
      const json: NotificationTopicsBatchResult = resp.data;
      if (json.added) {
        for (const k of Object.keys(json.added)) {
          if (json.added[k]) this._serverTopics.add(k);
        }
      }
      if (json.removed) {
        for (const k of Object.keys(json.removed)) {
          // Deleted whether the answer is true or false, because the server holds the topic in
          // neither case - false means it was never held, or the session is gone - so keeping the
          // entry records a belief nothing will correct on this connection, and only misleads
          // getTopicSummary and any future reader of what this connection holds.
          this._serverTopics.delete(k);
        }
      }
      // READ LAST, so a refusal is the final word on its topic. The contract says a refused topic
      // is also false in `added` (NotificationApiController.ManageTopics), so the loop above
      // already holds nothing for it - but reading refusals last means that stays true even if the
      // server ever contradicts itself, and the asymmetry matters: a false in `added` is the one
      // signal we retry, a refusal is the one we must never retry.
      if (json.refused) {
        for (const topic of Object.keys(json.refused)) {
          this._handleRefusedTopic(topic, json.refused[topic]);
        }
      }
      this._batchRetryAttempt = 0; // success resets backoff
    } catch (e) {
      console.warn('Batch topic update network error', e);
      this._requeueFailed(finalAdds, finalRemoves);
      this._scheduleRetry();
      retryScheduled = true;
    } finally {
      this._isFlushingBatch = false;
      for (const a of finalAdds) this._inflightAdds.delete(a);
      // THE FOLLOW-UP (U52). A topic queued while this request was in flight had its own flush
      // fire into the guard above and return, and nothing ran it again: the product page asked for
      // its own topic, then its three report lists a moment later, and when the first request
      // outlasted the 10 ms batch delay the lists were never asked for (SSE-AUTH-028, final gate
      // on da2bb528). So whatever is still queued is sent now. Not after a failure, whose retry is
      // already scheduled with its backoff and will carry the queued topics with it.
      if (!retryScheduled && this._pendingBatch.size > 0) {
        this._scheduleBatchFlush(this._batchDelayMs);
      }
    }
  }

  /**
   * A topic the server refused: say so once, hold nothing, and never ask for it again.
   *
   * A refusal is NOT a failure to be recovered from. This client only asks for a topic on a screen
   * the person may open, so being refused one means the client asked for something it had no
   * business asking for - a defect in our own code, and a silent one, because the screen still
   * renders and only the live updates that never arrive are missing (D-132, @SSE-AUTH-013). The
   * marker is what turns that silence into something a journey or a unit test trips over.
   *
   * CLEARING THE REF COUNT IS THE LOAD-BEARING LINE, not tidying up. Three separate obligations
   * come out of that one delete: `_resubscribeTopics` re-adds from this map, so a cleared count is
   * what stops the topic being asked for again when the connection reopens; `subscribeTopic`'s
   * "already logically subscribed" short-circuit reads the same map, so a later subscribe of the
   * same topic can make a fresh network add rather than being swallowed HERE; and
   * `unsubscribeTopic` returns early on a topic it has no count for, so nobody sends a remove for
   * something that was never held. Two components sharing the topic therefore lose it together,
   * which is right: the server refused the topic, not one of its subscribers.
   *
   * THE LAYER ABOVE IS TOLD, so that "not held" is true everywhere rather than only here. Every
   * production caller arrives through `SseHub.subscribeTopic`, which calls down to this class only
   * for the FIRST handler of a topic (sse-hub.ts:183-190). If the hub kept its handler set for a
   * refused topic, that gate would swallow every later subscribe and the fresh add described above
   * could never happen - the topic would read as held in the hub and as absent here, and the two
   * layers would disagree permanently. So the refusal is announced through `onTopicRefused` and
   * the hub drops its own entry: every component's handler for that topic goes together, because
   * the server refused the TOPIC and not one of its subscribers.
   *
   * THE CLIENT PUSHES AND THE HUB DOES NOT POLL, because a refusal is an event with a moment
   * rather than a state anything can read back. It is named on exactly one batch answer and never
   * repeated, and this method then sweeps all three stores, so a poller arriving afterwards would
   * find a topic indistinguishable from one nobody ever asked for. Pushing also keeps the
   * dependency pointing the way it already does: the hub knows the client, the client knows only
   * that somebody wanted to be told.
   *
   * THE PENDING BATCH IS THE THIRD STORE, and it is not redundant with the other two.
   * `_flushBatch` empties `_pendingBatch` BEFORE it awaits the answer, so anything queued while
   * the request is in flight was queued against a request whose answer had not arrived: a stream
   * that reopens inside that window runs `_resubscribeTopics` against a ref count still standing
   * and queues a fresh add. Without this delete that entry outlives the refusal that answers it,
   * and the next flush - triggered by any unrelated topic a person's navigation joins - re-asks
   * and logs the marker a second time. Only a queued ADD is dropped: a queued REMOVE is kept,
   * because the server may still hold this topic from an earlier successful add that the refusal
   * says nothing about - the controller's refusal arm never calls `TryRemoveTopic` - and dropping
   * it would leave the server delivering to a connection that believes it holds nothing.
   *
   * Nothing is re-queued. `_requeueFailed` exists for the whole request failing - a lost session,
   * a dead network - which is a condition that passes; a refusal is a decision that will be taken
   * again identically, so retrying it would log the marker on every flush and never succeed.
   *
   * The sentence is resolved before it is logged. It arrives on a 200 body, which is outside the
   * one seam that resolves server-composed text (`buildFailure` in agwasuite-api.ts runs only for
   * a FAILED response), so without this the console would carry `{{term loginUser.lowerPlural}}`
   * to the person reading it.
   */
  private _handleRefusedTopic(topic: string, message: string) {
    this._serverTopics.delete(topic);
    this._topicRefCounts.delete(topic);
    if (this._pendingBatch.get(topic) === 'add')
      this._pendingBatch.delete(topic);
    // AFTER the sweep, so a listener never sees a client that still half-holds the topic.
    this._notifyTopicRefused(topic);
    console.error(
      `${REFUSED_TOPIC_SENTINEL} ${topic}: ${replaceTerms(message)}`
    );
  }

  /**
   * Puts a failed batch back to be retried. A change queued since it was sent is newer and wins,
   * and an add for a topic nobody holds any more is not re-asked for (#398 C14 d): the page that
   * wanted it unsubscribed while it was in flight.
   */
  private _requeueFailed(adds: string[], removes: string[]) {
    for (const a of adds)
      if (this._topicRefCounts.has(a) && !this._pendingBatch.has(a))
        this._pendingBatch.set(a, 'add');
    for (const r of removes)
      if (!this._pendingBatch.has(r)) this._pendingBatch.set(r, 'remove');
  }

  private _scheduleRetry() {
    this._retryPending = true;
    this._batchRetryAttempt++;
    const delay = Math.min(
      this._baseRetryDelayMs * 2 ** (this._batchRetryAttempt - 1),
      this._maxBatchRetryDelayMs
    );
    this._scheduleBatchFlush(delay);
  }

  private _scheduleBatchFlush(delay: number) {
    if (this._batchTimer) clearTimeout(this._batchTimer);
    this._batchTimer = setTimeout(() => this._flushBatch(), delay);
  }

  private _isOpen(): boolean {
    // EventSource.readyState: 0 = CONNECTING, 1 = OPEN, 2 = CLOSED
    const src = this._source;
    return !!src && src.readyState === 1;
  }

  disconnect() {
    const hadConnection = !!this._source || !!this._reconnectTimer;
    this._source?.close();
    this._source = undefined;
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer);
      this._reconnectTimer = undefined;
    }
    this._nextReconnectAt = undefined;
    // Closed on purpose, with nothing retrying. Only when there was something to close, so the
    // disconnect every connect performs first does not announce a closure that never happened.
    if (hadConnection) this._notifyConnection('closed');
  }

  /**
   * Close the stream and never open it again in this page (APP-UPDATE-001, rule 1).
   *
   * Called when the new-version gate is shown. The gate blocks the page, so nothing the stream
   * delivers can be used, and a stream left open keeps this tab on the old service worker: in
   * Chrome on 2026-09-28 a waiting worker stayed waiting until the tab's stream closed. Every path
   * that would open a connection (a sign-in, the backoff timer, the health monitor's reconnectNow, a
   * connect still waiting on its tab session) checks the flag, so nothing reopens it behind the
   * gate. Only a reload clears it, and the reload is what the gate is for.
   */
  stopForGood() {
    this._stoppedForGood = true;
    this.disconnect();
  }
  addEventListener(eventNames: string | string[], event: EventSourceHandler) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];

    events.forEach((_eventName) => {
      const eventName = _eventName === '' ? _all_ : _eventName;

      let eventArray: EventSourceHandler[] = this._events[eventName];
      if (!eventArray) {
        eventArray = [];
        this._events[eventName] = eventArray;
      }
      if (!eventArray.find((x) => x === event)) eventArray.push(event);
    });
  }
  removeEventListener(
    eventNames: string | string[],
    event: EventSourceHandler
  ) {
    const events = Array.isArray(eventNames) ? eventNames : [eventNames];
    events.forEach((_eventName) => {
      const eventName = _eventName === '' ? _all_ : _eventName;
      const eventArray: EventSourceHandler[] = this._events[eventName];
      this._events[eventName] = eventArray?.filter(
        (binder) => !(binder === event)
      );
    });
  }
}
